import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './About.css';

const About = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  // IES Framework Cards - Impact first (what stakeholders care about most)
  const iesFramework = [
    {
      letter: 'I',
      title: 'Impactful',
      subtitle: 'Measurable Business Value',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
        </svg>
      ),
      points: [
        '5 successful enterprise POCs delivered',
        'Solutions across Healthcare, Finance, Telecom',
        'Measurable ROI through responsible ML optimization',
      ]
    },
    {
      letter: 'E',
      title: 'Ethical',
      subtitle: 'Responsible Data Usage',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      ),
      points: [
        'Patented Differential Privacy algorithms (3 patents)',
        'GDPR, HIPAA, CCPA compliance expertise',
        'Data anonymization without sacrificing utility',
      ]
    },
    {
      letter: 'S',
      title: 'Scalable',
      subtitle: 'Production-Ready Systems',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      ),
      points: [
        'Hybrid cloud architecture (AWS, WatsonX)',
        'Docker/Databricks marketplace deployments',
        'Enterprise-grade ML systems built for growth',
      ]
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <section id="about" className="about section" ref={ref}>
      <div className="container">
        <motion.div
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2>My Approach</h2>
          <p className="section-subtitle">
            The IES Framework™ for AI Product Development
          </p>
        </motion.div>

        <motion.div
          className="about-philosophy"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <p className="philosophy-text">
            The best AI products don't just work—they deliver <strong>measurable business outcomes</strong>, 
            handle data the right way, and scale with your growth. My IES Framework ensures every solution 
            I architect is <em>Impactful</em>, <em>Ethical</em>, and <em>Scalable</em>.
          </p>
        </motion.div>

        <motion.div
          className="eis-framework-grid"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {iesFramework.map((pillar, index) => (
            <motion.div
              key={index}
              className={`eis-card eis-${pillar.letter.toLowerCase()}`}
              variants={itemVariants}
            >
              <div className="eis-card-header">
                <span className="eis-card-letter">{pillar.letter}</span>
                <div className="eis-card-icon">{pillar.icon}</div>
              </div>
              <h3 className="eis-card-title">{pillar.title}</h3>
              <p className="eis-card-subtitle">{pillar.subtitle}</p>
              <ul className="eis-card-points">
                {pillar.points.map((point, i) => (
                  <li key={i}>{point}</li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="about-current"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className="current-role">
            <span className="current-label">Currently</span>
            <h3>AI/ML Technical Product Manager at IBM</h3>
            <p>
              Leading GenAI product development for telecom clients using WatsonX. 
              Developing time-series AI solutions for 4G RAN network congestion prediction 
              and financial asset value projections.
            </p>
          </div>
          <div className="about-industries">
            <h4>Industries I've Impacted</h4>
            <div className="industry-grid">
              {[
                { name: 'Healthcare', desc: 'Privacy-compliant ML for biotech & medical devices' },
                { name: 'Finance', desc: 'Credit scoring & asset prediction models' },
                { name: 'Telecom', desc: 'GenAI for network optimization' },
                { name: 'Automotive', desc: 'Computer vision for AR/HUD systems' },
              ].map((industry, index) => (
                <div key={index} className="industry-item">
                  <span className="industry-name">{industry.name}</span>
                  <span className="industry-desc">{industry.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;

